import { useCallback, useEffect, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchUnreadCount } from '../../api/comments';
import { ConversationModal, type ResolvedUser, type PostPreview } from './ConversationModal';
import styles from './CommentIndicator.module.css';

// =============================================================================
// CommentIndicator (DEV-720 Phase 1). Opens the ConversationModal for its anchor.
//
// Two variants:
//   'compact' (default) — small icon + count for dense rows (Role Digest table).
//   'bar' — a large social-style action button (icon + count + label) for feed /
//           Journal post cards.
//
// Phase 1 refreshes the count on mount and on modal close. Live SignalR
// (CommentCountChanged) wiring is a follow-up.
// =============================================================================

interface CommentIndicatorProps {
  anchorType: string;
  anchorId: string;
  title?: string;
  variant?: 'compact' | 'bar';
  /** The underlying post, pinned at the top of the modal. */
  post?: PostPreview;
  resolveUser?: (guid: string) => ResolvedUser | undefined;
  className?: string;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}

export function CommentIndicator({
  anchorType,
  anchorId,
  title,
  variant = 'compact',
  post,
  resolveUser,
  className,
}: CommentIndicatorProps) {
  const { t } = useAppTranslation('common');
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(() => {
    fetchUnreadCount(anchorType, anchorId)
      .then((r) => {
        setTotal(r.totalCount);
        setUnread(r.unreadCount);
      })
      .catch(() => {});
  }, [anchorType, anchorId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const close = () => {
    setOpen(false);
    refresh();
  };

  const isBar = variant === 'bar';

  return (
    <>
      <button
        type="button"
        className={`${styles.indicator} ${isBar ? styles.bar : styles.compact} ${unread > 0 ? styles.hasUnread : ''} ${className ?? ''}`}
        title={title ?? t('comments.unreadTitle')}
        aria-label={t('comments.unreadTitle')}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <i className="ti ti-message-circle" aria-hidden="true" />
        {isBar && <span className={styles.label}>{t('comments.unreadTitle')}</span>}
        {total > 0 && <span className={styles.count}>{formatCount(total)}</span>}
        {unread > 0 && <span className={styles.dot} aria-hidden="true" />}
      </button>

      <ConversationModal
        anchorType={anchorType}
        anchorId={anchorId}
        open={open}
        onClose={close}
        title={title}
        post={post}
        resolveUser={resolveUser}
      />
    </>
  );
}

export default CommentIndicator;
